"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { useInvoiceStore } from "@/lib/invoice/store";
import { computeTotals, financialYear, fmtMoney, invoiceStatus, num, todayISO } from "@/lib/invoice/utils";
import { Button, Card, Loading, PageHeader, Select, Stat, StatusBadge, cx } from "@/components/Invoice/ui";
import { saleAmount } from "@/lib/invoice/sales";
import { invoiceTags } from "@/lib/invoice/tags";
import { Combobox } from "@/components/Invoice/ui";
import { AiInsightsCard } from "@/components/Invoice/AskAI";
import TrendingIcon from "@mui/icons-material/TrendingUpRounded";
import ScheduleIcon from "@mui/icons-material/ScheduleRounded";
import SavingsIcon from "@mui/icons-material/SavingsOutlined";
import PendingIcon from "@mui/icons-material/PendingActionsOutlined";

function last12Months() {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 11; i >= 0; i--) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export default function Dashboard() {
  const store = useInvoiceStore();
  const currencies = useMemo(() => {
    const s = new Set([store.settings.defaults.currency]);
    store.invoices.forEach((i) => s.add(i.currency));
    return [...s];
  }, [store.invoices, store.settings]);
  const [cur, setCur] = useState(null);
  const currency = cur || currencies[0];
  const [period, setPeriod] = useState("fy");
  const [clientF, setClientF] = useState("");
  const [tagF, setTagF] = useState("");
  const [brandF, setBrandF] = useState("");
  const filtered = clientF || tagF || brandF || period !== "fy";

  const data = useMemo(() => {
    const fy = financialYear(todayISO());
    const thisMonth = todayISO().slice(0, 7);
    // period filter → [from, to] on invoice date
    const today = todayISO();
    const monthsAgo = (n) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - n);
      return d.toISOString().slice(0, 7) + "-01";
    };
    const fyStart = (f) => `20${f.slice(2, 4)}-04-01`;
    const lastFy = financialYear(`${Number(fy.slice(0, 4)) - 1}-06-01`);
    const range = {
      fy: [fyStart(fy), "9999"],
      lastfy: [fyStart(lastFy), `${Number(fy.slice(0, 4))}-03-31`],
      month: [thisMonth + "-01", "9999"],
      lastmonth: [monthsAgo(1), thisMonth + "-00"],
      "3m": [monthsAgo(2), "9999"],
      "12m": [monthsAgo(11), "9999"],
      all: ["0000", "9999"],
    }[period] || ["0000", "9999"];
    const inRange = (d) => (d || "") >= range[0] && (d || "") <= range[1];
    const rows = store.invoices
      .filter((i) => ["invoice", "credit"].includes(i.docType || "invoice") && i.currency === currency)
      .filter((i) => (!clientF || i.clientId === clientF) && (!brandF || (i.brandId || store.settings.defaultBrandId) === brandF) && (!tagF || invoiceTags(i).includes(tagF)))
      .map((inv) => {
        const t = computeTotals(inv);
        return { inv, t, status: invoiceStatus(inv, t), sign: inv.docType === "credit" ? -1 : 1 };
      })
      .filter((r) => r.status !== "cancelled");
    const issued = rows.filter((r) => r.status !== "draft"); // invoices + credit notes
    const billed = issued.filter((r) => r.sign > 0);
    const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);
    const months = last12Months().map((m) => ({
      m,
      billed: sum(issued.filter((r) => r.inv.date?.startsWith(m)), (r) => r.sign * r.t.total),
      paid: sum(billed, (r) => sum((r.inv.payments || []).filter((p) => !p.creditNoteId && p.date?.startsWith(m)), (p) => num(p.amount))),
    }));
    const byClient = {};
    for (const r of issued.filter((r) => inRange(r.inv.date))) {
      const k = r.inv.billTo?.name || "—";
      byClient[k] = (byClient[k] || 0) + r.sign * r.t.total;
    }
    return {
      fy,
      fyBilled: sum(issued.filter((r) => inRange(r.inv.date)), (r) => r.sign * r.t.total),
      periodReceived: sum(billed, (r) => sum((r.inv.payments || []).filter((p) => !p.creditNoteId && inRange(p.date)), (p) => num(p.amount))),
      count: issued.filter((r) => r.sign > 0 && inRange(r.inv.date)).length,
      outstanding: sum(billed, (r) => Math.max(0, r.t.balance)),
      overdue: billed.filter((r) => r.status === "overdue"),
      open: billed.filter((r) => r.t.balance > 0).sort((a, b) => (a.inv.dueDate || a.inv.date).localeCompare(b.inv.dueDate || b.inv.date)),
      paidThisMonth: sum(billed, (r) => sum((r.inv.payments || []).filter((p) => !p.creditNoteId && p.date?.startsWith(thisMonth)), (p) => num(p.amount))),
      drafts: rows.filter((r) => r.status === "draft").length,
      credits: sum(issued.filter((r) => r.sign < 0 && inRange(r.inv.date)), (r) => r.t.total),
      months,
      topClients: Object.entries(byClient).sort((a, b) => b[1] - a[1]).slice(0, 6),
    };
  }, [store.invoices, store.settings.defaultBrandId, currency, period, clientF, tagF, brandF]);

  if (!store.ready) return <Loading />;

  const clientCur = (cid) => store.clients.find((c) => c.id === cid)?.currency || store.settings.defaults.currency;
  const runRate = store.projects
    .filter((p) => p.status === "active" && p.billing === "monthly")
    .filter((p) => !store.clients.find((c) => c.id === p.clientId)?.archived)
    .filter((p) => clientCur(p.clientId) === currency && (!clientF || p.clientId === clientF) && (!tagF || p.tag === tagF))
    .reduce((s, p) => s + num(p.rate) * (num(p.qty) || 1), 0);
  const unbilled = (store.sales || []).filter((x) => !x.invoiceId && clientCur(x.clientId) === currency && (!clientF || x.clientId === clientF) && (!tagF || x.tag === tagF));
  const unbilledTotal = unbilled.reduce((s, x) => s + saleAmount(x), 0);
  const m = (n) => fmtMoney(n, currency);
  const empty = store.clients.length === 0 && store.invoices.length === 0;

  // Retainer clients not yet billed for last month.
  const lm = new Date();
  lm.setDate(1);
  lm.setMonth(lm.getMonth() - 1);
  const lastMonth = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, "0")}`;
  const lastMonthName = new Date(lastMonth + "-01").toLocaleDateString("en-GB", { month: "long" });
  const toBill = store.clients
    .filter((c) => !c.archived)
    .filter((c) => store.projects.some((p) => p.clientId === c.id && p.status === "active" && p.billing === "monthly"))
    .filter((c) => !store.invoices.some((i) => i.clientId === c.id && i.periodMonth === lastMonth && i.status !== "cancelled"));

  const todos = [
    toBill.length > 0 && { tone: "brand", text: `${toBill.length} retainer client${toBill.length > 1 ? "s" : ""} not billed for ${lastMonthName}`, action: "Run month-end", href: "/invoice/invoices?run=1" },
    unbilled.length > 0 && { tone: "amber", text: `${unbilled.length} unbilled sale${unbilled.length > 1 ? "s" : ""} worth ${m(unbilledTotal)}`, action: "Review sales", href: "/invoice/sales" },
    data.drafts > 0 && { tone: "slate", text: `${data.drafts} draft invoice${data.drafts > 1 ? "s" : ""} to finalise`, action: "Open drafts", href: "/invoice/invoices?status=draft" },
    data.overdue.length > 0 && { tone: "red", text: `${data.overdue.length} overdue invoice${data.overdue.length > 1 ? "s" : ""}`, action: "Chase payment", href: "/invoice/invoices?status=overdue" },
  ].filter(Boolean);

  const hour = new Date().getHours();
  const greet = hour < 5 ? "Working late" : hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const rawName = String(store.settings.ownerName || store.currentUser || "").trim();
  const firstName = rawName ? rawName.split(/\s+/)[0].replace(/^./, (c) => c.toUpperCase()) : "";
  const PERIODS = [
    ["fy", `This financial year (${data.fy})`],
    ["lastfy", "Last financial year"],
    ["month", "This month"],
    ["lastmonth", "Last month"],
    ["3m", "Last 3 months"],
    ["12m", "Last 12 months"],
    ["all", "All time"],
  ];
  const periodLabel = PERIODS.find(([k]) => k === period)?.[1] || "";

  return (
    <div>
      <PageHeader
        eyebrow={new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
        title={firstName ? `${greet}, ${firstName}` : greet}
        subtitle={`Here’s how ${store.defaultBrand?.name || "your business"} is doing · ${periodLabel.toLowerCase()}`}
        actions={
          <>
            <Link href="/invoice/sales"><Button variant="secondary">Record sale</Button></Link>
            <Link href="/invoice/invoices/edit"><Button>New invoice</Button></Link>
          </>
        }
      />

      {!empty && (
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl bg-white p-3 shadow-xs ring-1 ring-slate-200/80" data-testid="dashboard-filters">
          <Select className="!w-56" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period">
            {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
          <div className="w-60">
            <Combobox
              strict
              value={clientF}
              onChange={setClientF}
              options={[{ value: "", label: "All clients" }, ...[...store.clients].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name }))]}
              placeholder="All clients"
              testId="dash-client"
            />
          </div>
          <Select className="!w-48" value={tagF} onChange={(e) => setTagF(e.target.value)} aria-label="Category">
            <option value="">All categories</option>
            {store.settings.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
          {store.brands.length > 1 && (
            <Select className="!w-52" value={brandF} onChange={(e) => setBrandF(e.target.value)} aria-label="Business profile">
              <option value="">All business profiles</option>
              {store.brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          )}
          {currencies.length > 1 && (
            <Select className="!w-24" value={currency} onChange={(e) => setCur(e.target.value)} aria-label="Currency">
              {currencies.map((c) => <option key={c}>{c}</option>)}
            </Select>
          )}
          {filtered && (
            <button className="ml-auto text-xs font-medium text-slate-500 hover:text-slate-900" onClick={() => { setPeriod("fy"); setClientF(""); setTagF(""); setBrandF(""); }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {empty && (
        <div className="mb-6 overflow-hidden rounded-2xl bg-ink-900 p-6 text-white shadow-pop sm:p-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-300">Get started</div>
          <div className="mt-1 text-xl font-semibold tracking-tight">Set up Invoice Studio in three steps</div>
          <ol className="mt-4 grid gap-3 text-sm text-slate-300 sm:grid-cols-3">
            <li className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10"><b className="text-white">1. Your brand</b><br />Logo, signature, bank and template in <Link className="text-brand-300 underline" href="/invoice/settings">Settings</Link>.</li>
            <li className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10"><b className="text-white">2. Clients & projects</b><br />Import your August client with all 79 projects, or <Link className="text-brand-300 underline" href="/invoice/import">import old PDFs</Link>.</li>
            <li className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10"><b className="text-white">3. Bill monthly</b><br />Record sales during the month, then run month-end invoicing.</li>
          </ol>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={() => store.loadSample()}>Import August 2026 client & projects</Button>
            <Link href="/invoice/clients?new=1"><Button variant="secondary">Add a client</Button></Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Stat label={period === "fy" ? `Billed · FY ${data.fy}` : `Billed · ${periodLabel}`} value={m(data.fyBilled)} sub={data.credits ? `after ${m(data.credits)} credit notes` : `${data.count} invoice${data.count === 1 ? "" : "s"} · ${m(runRate)} monthly recurring`} icon={<TrendingIcon sx={{ fontSize: 18 }} />} />
        <Stat label="Outstanding" value={m(data.outstanding)} tone={data.outstanding ? "red" : undefined} sub={data.overdue.length ? `${data.overdue.length} overdue · ${data.open.length} unpaid` : `${data.open.length} unpaid invoice(s)`} icon={<ScheduleIcon sx={{ fontSize: 18 }} />} href="/invoice/invoices?status=overdue" />
        <Stat label={period === "fy" ? "Received this month" : `Received · ${periodLabel}`} value={m(period === "fy" ? data.paidThisMonth : data.periodReceived)} tone="green" sub="payments recorded" icon={<SavingsIcon sx={{ fontSize: 18 }} />} />
        <Stat label="Unbilled sales" value={m(unbilledTotal)} tone={unbilled.length ? "amber" : undefined} sub={`${unbilled.length} entr${unbilled.length === 1 ? "y" : "ies"} waiting for month end`} icon={<PendingIcon sx={{ fontSize: 18 }} />} href="/invoice/sales" />
      </div>

      {!empty && (
        <div className="mt-6">
          <AiInsightsCard />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card title="Billed vs received" subtitle="Last 12 months" className="xl:col-span-2">
          <RevenueChart months={data.months} fmt={m} />
        </Card>

        <Card title="To do" subtitle={todos.length ? `${todos.length} thing${todos.length > 1 ? "s" : ""} need attention` : "You’re all caught up"}>
          {todos.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center text-center text-sm text-slate-500">
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">✓</div>
              Nothing pending. Nice work.
            </div>
          ) : (
            <ul className="space-y-2.5">
              {todos.map((t) => (
                <li key={t.text} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                  <span className={cx("h-8 w-1 shrink-0 rounded-full", { brand: "bg-brand-500", amber: "bg-amber-400", slate: "bg-slate-400", red: "bg-red-500" }[t.tone])} />
                  <div className="min-w-0 flex-1 text-sm text-slate-700">{t.text}</div>
                  <Link href={t.href} className="shrink-0 text-xs font-semibold text-brand-700 hover:underline">
                    {t.action} →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card title="Unpaid invoices" subtitle="Oldest due first" className="xl:col-span-2" flush actions={<Link className="text-xs font-semibold text-brand-700 hover:underline" href="/invoice/invoices">View all →</Link>}>
          {data.open.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">Nothing outstanding.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {data.open.slice(0, 8).map(({ inv, t, status }) => (
                  <tr key={inv.id} className="transition-colors hover:bg-slate-50/70">
                    <td className="py-3 pl-5 pr-3">
                      <Link className="font-medium text-slate-900 hover:text-brand-700" href={`/invoice/invoices/view?id=${inv.id}`}>{inv.number}</Link>
                      <div className="text-xs text-slate-500">{inv.billTo?.name}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500">{inv.dueDate ? `Due ${inv.dueDate}` : `Issued ${inv.date}`}</td>
                    <td className="px-3 py-3 text-right font-semibold tabular-nums">{m(t.balance)}</td>
                    <td className="py-3 pl-3 pr-5 text-right"><StatusBadge status={status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title={`Top clients`} subtitle={period === "fy" ? `FY ${data.fy}` : periodLabel}>
          {data.topClients.length === 0 ? (
            <div className="text-sm text-slate-500">No invoices this year yet.</div>
          ) : (
            <ul className="space-y-3.5 text-sm">
              {data.topClients.map(([name, v]) => (
                <li key={name}>
                  <div className="flex justify-between gap-2">
                    <span className="truncate text-slate-700">{name}</span>
                    <span className="font-semibold tabular-nums text-slate-900">{m(v)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-[#2a78d6]" style={{ width: `${Math.max(2, (v / data.topClients[0][1]) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

// Grouped bars (billed vs received) per month – validated categorical slots 1 & 2,
// thin marks with rounded data ends, 2px gap between bars, recessive grid, hover tooltip.
const SERIES = [
  { key: "billed", label: "Billed", color: "#2a78d6" },
  { key: "paid", label: "Received", color: "#eb6834" },
];

function niceMax(v) {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

function RevenueChart({ months, fmt }) {
  const [hover, setHover] = useState(null);
  const W = 720;
  const H = 240;
  const pad = { l: 56, r: 8, t: 12, b: 26 };
  const max = niceMax(Math.max(...months.map((x) => Math.max(x.billed, x.paid)), 0));
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const band = iw / months.length;
  const barW = Math.min(14, (band - 10) / 2);
  const y = (v) => pad.t + ih - (Math.max(0, v) / max) * ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const short = (v) => (v >= 1e6 ? `${+(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${+(v / 1e3).toFixed(1)}k` : String(+v.toFixed(v < 10 ? 2 : 0)));
  const hasData = months.some((x) => x.billed || x.paid);
  const label = (mm) => new Date(mm + "-01").toLocaleDateString("en-GB", { month: "short" });
  const bar = (x, v, color) => {
    const h = Math.max(0, pad.t + ih - y(v));
    if (h <= 0) return null;
    const r = Math.min(4, h, barW / 2);
    const top = pad.t + ih - h;
    // rounded top corners, square at the baseline
    return <path d={`M${x},${pad.t + ih} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${pad.t + ih} Z`} fill={color} />;
  };
  return (
    <div className="relative">
      <div className="mb-3 flex items-center gap-4 text-xs text-slate-600">
        {SERIES.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Billed and received per month for the last 12 months">
        {(hasData ? ticks : [0]).map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="#e7e9ee" strokeWidth="1" />
            <text x={pad.l - 8} y={y(t) + 3.5} textAnchor="end" fontSize="10.5" fill="#64748b">{short(t)}</text>
          </g>
        ))}
        {months.map((mo, i) => {
          const x0 = pad.l + i * band + (band - (barW * 2 + 2)) / 2;
          return (
            <g key={mo.m}>
              {hover === i && <rect x={pad.l + i * band + 2} y={pad.t} width={band - 4} height={ih} rx="6" fill="#f1f5f9" />}
              {bar(x0, mo.billed, SERIES[0].color)}
              {bar(x0 + barW + 2, mo.paid, SERIES[1].color)}
              <text x={pad.l + i * band + band / 2} y={H - 8} textAnchor="middle" fontSize="10.5" fill={hover === i ? "#0f172a" : "#64748b"}>
                {label(mo.m)}
              </text>
              <rect x={pad.l + i * band} y={pad.t} width={band} height={ih + pad.b} fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
            </g>
          );
        })}
      </svg>
      {!hasData && (
        <div className="pointer-events-none absolute inset-0 top-8 flex items-center justify-center">
          <div className="rounded-xl bg-white/90 px-4 py-2 text-sm text-slate-500 ring-1 ring-slate-200">Your first finalised invoice will appear here</div>
        </div>
      )}
      {hover != null && hasData && (
        <div
          className="pointer-events-none absolute top-8 z-10 min-w-[170px] -translate-x-1/2 rounded-xl bg-ink-900 px-3 py-2 text-xs text-white shadow-pop"
          style={{ left: `${((pad.l + hover * band + band / 2) / W) * 100}%` }}
        >
          <div className="mb-1 font-semibold">{new Date(months[hover].m + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
          {SERIES.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-1.5 text-slate-300">
                <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
                {s.label}
              </span>
              <span className="font-semibold tabular-nums">{fmt(months[hover][s.key])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
